import { Component } from '@theme/component';

/**
 * A scroll-snap testimonials carousel driven by prev/next arrows (desktop)
 * and native touch/trackpad scrolling (mobile). Arrows disable at the ends
 * and hide when the cards don't overflow.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrollable card track.
 * @property {HTMLButtonElement} [previous] - Previous arrow.
 * @property {HTMLButtonElement} [next] - Next arrow.
 * @property {HTMLElement} [arrows] - The arrows wrapper.
 * @extends Component<Refs>
 */
class TestimonialsCarousel extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();

    const { track, previous, next } = this.refs;
    previous?.addEventListener('click', this.#previous);
    next?.addEventListener('click', this.#next);
    track.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver.observe(track);

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { track, previous, next } = this.refs;
    previous?.removeEventListener('click', this.#previous);
    next?.removeEventListener('click', this.#next);
    track?.removeEventListener('scroll', this.#update);
    this.#resizeObserver.disconnect();
  }

  #next = () => this.#scrollByCards(1);
  #previous = () => this.#scrollByCards(-1);

  /**
   * Scrolls the track by one card width in the given direction.
   * @param {number} direction - 1 for next, -1 for previous.
   */
  #scrollByCards(direction) {
    const { track } = this.refs;
    const card = track.querySelector(':scope > *');
    if (!(card instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = card.getBoundingClientRect().width + gap;

    track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  #update = () => {
    const { track, previous, next, arrows } = this.refs;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const overflowing = maxScroll > 1;

    if (arrows) arrows.hidden = !overflowing;
    if (previous) previous.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= maxScroll - 1;
  };

  #resizeObserver = new ResizeObserver(() => this.#update());
}

if (!customElements.get('testimonials-carousel')) {
  customElements.define('testimonials-carousel', TestimonialsCarousel);
}
